import { Dropdown } from 'antd';
import { setCookie } from 'cookies-next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineGlobal } from 'react-icons/ai';

import {
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE,
  LOCALE_NATIVE_NAMES,
  SUPPORTED_LOCALES,
  SupportedLocale,
} from '@/lib/i18n/settings';

interface LanguageSwitcherProps {
  // 'floating': desktop-only pill pinned to the bottom inline-start corner.
  // 'header': compact icon button for the mobile header, where a floating
  // element would hover over content and collide with iOS browser chrome.
  variant?: 'floating' | 'header';
  iconColor?: string;
}

const LanguageSwitcher = ({ variant = 'floating', iconColor }: LanguageSwitcherProps) => {
  const { t, i18n } = useTranslation();
  const currentLocale = (i18n.language as SupportedLocale) || 'en';

  const handleSelect = useCallback(
    ({ key }: { key: string }) => {
      if (key === i18n.language) return;
      setCookie(LOCALE_COOKIE, key, { maxAge: LOCALE_COOKIE_MAX_AGE, path: '/', sameSite: 'lax' });
      i18n.changeLanguage(key);
    },
    [i18n],
  );

  const menu = {
    items: SUPPORTED_LOCALES.map((locale) => ({
      key: locale,
      label: (
        <span lang={locale} className={locale === currentLocale ? 'font-bold' : undefined}>
          {LOCALE_NATIVE_NAMES[locale]}
        </span>
      ),
    })),
    selectedKeys: [currentLocale],
    onClick: handleSelect,
  };

  if (variant === 'header') {
    return (
      <Dropdown placement="bottom" trigger={['click']} menu={menu}>
        <button
          type="button"
          aria-label={t('languageSwitcher.label')}
          className="flex md:hidden items-center justify-center cursor-pointer border-0 bg-transparent p-2"
          style={{ color: iconColor || 'inherit' }}
        >
          <AiOutlineGlobal size={22} />
        </button>
      </Dropdown>
    );
  }

  return (
    <div className="hidden md:block fixed bottom-6 start-6 z-[2001]">
      <Dropdown placement="top" trigger={['click']} menu={menu}>
        <button
          type="button"
          aria-label={t('languageSwitcher.label')}
          className="flex items-center gap-2 cursor-pointer rounded-full border border-solid border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black-text shadow-lg hover:shadow-xl transition-shadow"
        >
          <AiOutlineGlobal size={18} />
          <span lang={currentLocale}>{LOCALE_NATIVE_NAMES[currentLocale]}</span>
        </button>
      </Dropdown>
    </div>
  );
};

export default LanguageSwitcher;
